import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

/**
 * The import's layers over satellite imagery, from the admin preview tiles
 * (drafts until published). Tiles need the admin token, so it is added to
 * every request to the API by transformRequest.
 */

const API = process.env.REACT_APP_BASE_URL || "";
const SATELLITE = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const GLYPHS = "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf";

const GROUPS = [
  { key: "zones", label: "Zoning", layers: ["zones-fill", "zones-line"], on: true },
  { key: "villages", label: "Villages", layers: ["villages-line", "villages-label"], on: true },
  { key: "parcels", label: "Survey parcels (zoom 13+)", layers: ["parcels-line", "parcels-label"], on: true },
  { key: "tp", label: "TP plots (zoom 12+)", layers: ["tp-line", "tp-label"], on: false },
  { key: "roads", label: "Roads", layers: ["roads-line"], on: true },
];

const esc = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const area = (m2) =>
  !m2 ? "-" : m2 >= 10000 ? `${(m2 / 10000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} ha` : `${Math.round(m2).toLocaleString("en-IN")} m²`;

function popupHtml(features) {
  const pick = (layer) => features.find((f) => f.sourceLayer === layer)?.properties;
  const rows = [];
  const z = pick("dp_zones");
  if (z) rows.push(["Zone", `${esc(z.zone_code || "no code")} · ${esc(z.zone_name || z.fill_color)}`]);
  const p = pick("survey_parcels");
  if (p) rows.push(["Survey no.", esc(p.survey_no || (p.labels ? `? (${p.labels})` : "not read"))], ["Parcel area", area(p.area_sqm)]);
  const t = pick("tp_plots");
  if (t) rows.push(["TP plot", `${esc(t.tp_name)} · FP ${esc(t.fp_no || (t.labels ? `? (${t.labels})` : "not read"))}`]);
  const v = pick("village_boundaries");
  if (v) rows.push(["Village", esc(v.name || (v.labels ? `? (${v.labels})` : "not read"))]);
  const r = pick("roads");
  if (r) rows.push(["Road", `${esc(r.width_m ? `${Number(r.width_m)} m` : "width unknown")} · ${esc(r.road_status || "")}`]);
  if (!rows.length) return null;
  return `<table style="font-size:12px;border-collapse:collapse">${rows
    .map(([k, val]) => `<tr><td style="color:#6b7280;padding:2px 10px 2px 0;white-space:nowrap">${k}</td><td>${val}</td></tr>`)
    .join("")}</table>`;
}

export default function PreviewMap({ importId, version, bounds }) {
  const container = useRef(null);
  const mapRef = useRef(null);
  const [visible, setVisible] = useState(() => Object.fromEntries(GROUPS.map((g) => [g.key, g.on])));

  useEffect(() => {
    if (!container.current) return undefined;
    const token = localStorage.getItem("auth-token");
    const tiles = `${API}/maps/gis-imports/${importId}/tiles/{z}/{x}/{y}?v=${encodeURIComponent(version || "")}`;

    const map = new maplibregl.Map({
      container: container.current,
      style: {
        version: 8,
        glyphs: GLYPHS,
        sources: {
          sat: { type: "raster", tiles: [SATELLITE], tileSize: 256, maxzoom: 19, attribution: "Imagery © Esri" },
          draft: { type: "vector", tiles: [tiles], minzoom: 8, maxzoom: 16 },
        },
        layers: [
          { id: "sat", type: "raster", source: "sat" },
          { id: "zones-fill", type: "fill", source: "draft", "source-layer": "dp_zones",
            paint: { "fill-color": ["coalesce", ["get", "fill_color"], "#9ca3af"], "fill-opacity": 0.5 } },
          { id: "zones-line", type: "line", source: "draft", "source-layer": "dp_zones", minzoom: 14,
            paint: { "line-color": "#1f2937", "line-width": 0.5, "line-opacity": 0.5 } },
          { id: "tp-line", type: "line", source: "draft", "source-layer": "tp_plots",
            paint: { "line-color": "#dc2626", "line-width": 1 } },
          { id: "parcels-line", type: "line", source: "draft", "source-layer": "survey_parcels",
            paint: { "line-color": "#111827", "line-width": ["interpolate", ["linear"], ["zoom"], 13, 0.3, 17, 1.2], "line-opacity": 0.8 } },
          { id: "roads-line", type: "line", source: "draft", "source-layer": "roads",
            paint: { "line-color": "#f97316", "line-width": 2, "line-dasharray": [3, 2] } },
          { id: "villages-line", type: "line", source: "draft", "source-layer": "village_boundaries",
            paint: { "line-color": "#1d4ed8", "line-width": 2.5 } },
          { id: "tp-label", type: "symbol", source: "draft", "source-layer": "tp_plots", minzoom: 16,
            layout: { "text-field": ["coalesce", ["get", "fp_no"], ""], "text-font": ["Noto Sans Regular"], "text-size": 11 },
            paint: { "text-color": "#991b1b", "text-halo-color": "#fff", "text-halo-width": 1.5 } },
          { id: "parcels-label", type: "symbol", source: "draft", "source-layer": "survey_parcels", minzoom: 15,
            layout: { "text-field": ["coalesce", ["get", "survey_no"], ""], "text-font": ["Noto Sans Regular"], "text-size": 11 },
            paint: { "text-color": "#111", "text-halo-color": "#fff", "text-halo-width": 1.5 } },
          { id: "villages-label", type: "symbol", source: "draft", "source-layer": "village_boundaries", maxzoom: 15,
            layout: { "text-field": ["coalesce", ["get", "name"], ""], "text-font": ["Noto Sans Regular"], "text-size": 14 },
            paint: { "text-color": "#1e3a8a", "text-halo-color": "#fff", "text-halo-width": 2 } },
        ],
      },
      center: [72.6, 22.4],
      zoom: 7,
      transformRequest: (url) =>
        API && url.startsWith(API) ? { url, headers: { Authorization: `Bearer ${token}` } } : { url },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");

    if (bounds?.west != null) {
      map.fitBounds([[bounds.west, bounds.south], [bounds.east, bounds.north]], { padding: 30, duration: 0 });
    }

    map.on("click", (e) => {
      const html = popupHtml(map.queryRenderedFeatures(e.point, {
        layers: ["zones-fill", "parcels-line", "tp-line", "villages-line", "roads-line"].filter((id) => map.getLayer(id)),
      }).concat(
        // Lines only hit near their edge, so also look up the polygons under the point.
        map.querySourceFeatures("draft", { sourceLayer: "survey_parcels" }).filter((f) => contains(f.geometry, e.lngLat)),
        map.querySourceFeatures("draft", { sourceLayer: "village_boundaries" }).filter((f) => contains(f.geometry, e.lngLat))
      ));
      if (html) new maplibregl.Popup({ maxWidth: "300px" }).setLngLat(e.lngLat).setHTML(html).addTo(map);
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Rebuilt when the import changes (a new build = a new version).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId, version]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const apply = () => {
      for (const g of GROUPS) {
        for (const id of g.layers) {
          if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", visible[g.key] ? "visible" : "none");
        }
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("styledata", apply);
    return undefined;
  }, [visible, version]);

  return (
    <div>
      <div className="d-flex flex-wrap gap-3 mb-2 small">
        {GROUPS.map((g) => (
          <Form.Check
            key={g.key}
            id={`gis-preview-${g.key}`}
            type="checkbox"
            label={g.label}
            checked={visible[g.key]}
            onChange={(e) => setVisible((v) => ({ ...v, [g.key]: e.target.checked }))}
          />
        ))}
      </div>
      <div ref={container} style={{ height: 520, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border-100, #e5e7eb)" }} />
      <div className="text-muted small mt-1">Click the map for what is there.</div>
    </div>
  );
}

/** Point in Polygon / MultiPolygon, ray casting (holes excluded). */
function contains(geometry, lngLat) {
  if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) return false;
  const pt = [lngLat.lng, lngLat.lat];
  const polys = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  const inRing = (ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      const aboveI = yi > pt[1];
      const aboveJ = yj > pt[1];
      const crosses = aboveI !== aboveJ;
      if (crosses && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };
  return polys.some((rings) => inRing(rings[0]) && !rings.slice(1).some(inRing));
}
